// reviewWorkflow.js — constantes y helper de transición de estado de una
// Respuesta de terreno, compartidos entre TerrainReviewQueue.vue (vista de
// tarjetas) y TerrainResponsesTable.vue (vista de tabla). Extraído de
// TerrainReviewQueue.vue, que antes los tenía como consts/métodos locales —
// duplicarlos en la tabla nueva habría arriesgado que ambas vistas queden
// desincronizadas si el flujo de revisión cambia.

// Transiciones que exigen comentario obligatorio antes de poder enviarse
// (Requirements 14.7, 14.8: comentario obligatorio en rechazo/retiro).
export const COMMENT_REQUIRED_TARGETS = ["rejected", "withdrawn"];

// Targets válidos por estado actual, reflejando reviewWorkflowEdges
// (services/terrain/review_workflow.go) del lado del servidor. "observed"
// queda fuera a propósito: esa transición exige adjuntar observaciones por
// pregunta, una UI que ninguna de las dos vistas ofrece todavía.
//
// Big Boss, 2026-08-19: se eliminó el paso obligatorio "en revisión" — el
// revisor pasa directo de enviada/corregida/retirada a aprobar o rechazar.
// "en_revision" se conserva solo para respuestas que ya estaban en ese
// estado antes del cambio (nada transiciona hacia él de nuevo).
export const AVAILABLE_TARGETS_BY_STATUS = {
  submitted: ["approved", "rejected"],
  corregida: ["approved", "rejected"],
  retirada: ["approved", "rejected"],
  en_revision: ["approved", "rejected"],
	rechazada: ["corrected"],
  aprobada: ["withdrawn"],
};

// Targets que además de "review" exigen la capacidad "publish"
// (services/terrain/review_workflow.go — aprobar/retirar). Ninguna vista
// gateaba esto por capacidad hasta ahora (solo por estado) — un usuario con
// review pero sin publish veía el botón y recibía 403 recién al hacer clic.
export const PUBLISH_REQUIRED_TARGETS = ["approved", "withdrawn"];

// Estados que todavía requieren acción del revisor, agrupados aparte de los
// ya resueltos (aprobada/rechazada/retirada).
export const PENDING_STATUSES = ["submitted", "en_revision", "corregida"];

// Estados de respuesta que admiten corrección de datos por el revisor (modo
// edición) — espejo de correctionEditableStatuses en
// services/terrain/correction_service.go. rechazada y retirada quedan
// cerradas; corregir NO cambia el estado (es ortogonal al workflow).
export const EDITABLE_STATUSES = ["submitted", "en_revision", "rechazada", "aprobada"];

const STATUS_LABELS = {
  submitted: "Enviada",
  en_revision: "En revisión",
  observada: "Observada",
  corregida: "Corregida",
  aprobada: "Aprobada",
  rechazada: "Rechazada",
  retirada: "Retirada",
};

export const TARGET_LABELS = {
  in_review: "Enviar a revisión",
  approved: "Aprobar",
  rejected: "Rechazar",
	corrected: "Marcar corregida",
  withdrawn: "Retirar",
};

export function statusLabel(status) {
  return STATUS_LABELS[status] || "No disponible";
}

export function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `terrain-transition-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// transitionErrorMessage: mismo criterio de mensaje amigable que ya usaba
// TerrainReviewQueue.vue — el único código de error que distingue es
// TERRAIN_VALIDATION_FAILED (comentario faltante), el resto cae a un
// mensaje genérico de reintentar.
export function transitionErrorMessage(error) {
  const code = error.response && error.response.data && error.response.data.code;
  return code === "TERRAIN_VALIDATION_FAILED"
    ? "Revise los campos indicados, el comentario es obligatorio para esta acción."
    : "No fue posible aplicar la transición. Intente nuevamente.";
}

// runTransition: encapsula el llamado a POST /responses/:id/transitions con
// su Idempotency-Key + If-Match (expectedVersion), igual que ya hacía
// TerrainReviewQueue.vue a mano en cada callsite. `response` solo necesita
// {id, resourceVersion}. Devuelve response.data ({status, resourceVersion}).
export async function runTransition(terrainApi, response, target, comment) {
  const payload = { target, expectedVersion: response.resourceVersion, comment: comment || undefined };
	if (target === "rejected") payload.observations = [{ questionId: "general", message: comment }];
  const result = await terrainApi.transitionResponse(response.id, payload, response.resourceVersion, createIdempotencyKey());
  return result.data;
}
